import { allowNativeNavigation } from "@/hooks/useNativeDraft";
import { isNativeApp } from "@/lib/native";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Bot, MessageSquare } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { usePortal } from "@/lib/PortalContext";
import NotificationCenter from "@/components/NotificationCenter";
import DarkModeToggle from "@/components/DarkModeToggle";

import { useAssistantNavigation } from "@/hooks/useAssistantNavigation";

const titles = {
  assistant: "Assistant",
  dashboard: "Dashboard",
  events: "Events",
  event: "Event",
  flights: "Flights",
  clock: "Clock In / Out",
  tasks: "Tasks",
  staff: "Staff",
  profile: "My Profile",
  availability: "My Availability",
  documents: "My Documents",
  "document-management": "Documents",
  training: "Training",
  notifications: "Notifications",
  "notification-settings": "Notification Settings",
  "notification-preferences": "Notification Preferences",
  admin: "Developer",
  "staff-profile": "Staff Profile",
};

export default function NativeHeader() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const assistant = useAssistantNavigation();
  const { isManagerView } = usePortal();
  const [editing, setEditing] = useState(false);
  const page = pathname.split("/")[1] || "dashboard";
  const back = () => {
    if (allowNativeNavigation())
      window.history.state?.idx > 0
        ? navigate(-1)
        : navigate(page === "event" ? "/events" : "/dashboard");
  };
  useEffect(() => {
    if (!isNativeApp()) return;
    let disposed = false;
    let listener;
    import("@capacitor/app")
      .then(async ({ App }) => {
        listener = await App.addListener("backButton", () => {
          const overlays = document.querySelectorAll(
            '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
          );
          if (overlays.length) {
            document.dispatchEvent(
              new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
            );
          } else if (!allowNativeNavigation()) return;
          else if (window.history.state?.idx > 0) navigate(-1);
          else if (pathname !== "/dashboard" && pathname !== "/")
            navigate("/dashboard");
          else App.minimizeApp();
        });
        if (disposed) listener.remove();
      })
      .catch(() => {});
    return () => {
      disposed = true;
      listener?.remove();
    };
  }, [navigate, pathname]);
  useEffect(() => {
    const viewport = window.visualViewport;
    let scrollTimer;
    let focusTimer;
    const textInput = (element) => element?.matches(
      'textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="file"]):not([type="range"]), [contenteditable="true"]',
    );
    const sync = () => {
      const active = document.activeElement;
      const focused = !!textInput(active);
      setEditing(focused);
      document.body.classList.toggle("native-editing", focused);
      // Capacitor can resize the body while the WebView viewport stays full height.
      const bodyHeight = document.body.style.height ? document.body.clientHeight : window.innerHeight;
      const height = Math.min(viewport?.height || window.innerHeight, bodyHeight || window.innerHeight);
      document.documentElement.style.setProperty(
        "--native-visible-height",
        `${height}px`,
      );
      clearTimeout(scrollTimer);
      if (focused) scrollTimer = setTimeout(() => {
        if (document.activeElement === active) active.scrollIntoView({ block: "nearest", behavior: "auto" });
      }, 120);
    };
    const onFocusOut = () => { clearTimeout(focusTimer); focusTimer = setTimeout(sync, 0); };
    const dismissOnOutsideClick = (event) => {
      if (textInput(document.activeElement) && !event.target.closest?.('input, textarea, label, [contenteditable="true"]')) {
        document.activeElement.blur();
      }
    };
    const bodyObserver = new ResizeObserver(sync);
    bodyObserver.observe(document.body);
    viewport?.addEventListener("resize", sync);
    window.addEventListener("resize", sync);
    document.addEventListener("focusin", sync);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("click", dismissOnOutsideClick, true);
    const guardLinks = (event) => {
      const anchor = event.target.closest?.("a[href]");
      if (anchor && !allowNativeNavigation()) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", guardLinks, true);
    sync();
    return () => {
      document.removeEventListener("click", guardLinks, true);
      viewport?.removeEventListener("resize", sync);
      window.removeEventListener("resize", sync);
      bodyObserver.disconnect();
      clearTimeout(scrollTimer);
      clearTimeout(focusTimer);
      document.removeEventListener("focusin", sync);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("click", dismissOnOutsideClick, true);
      document.body.classList.remove("native-editing");
      document.documentElement.style.removeProperty("--native-visible-height");
    };
  }, []);
  return (
    <header className="native-header">
      {!["dashboard", "events", "flights", "clock", "tasks"].includes(page) && (
        <button onClick={back} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
      )}
      <h1 className="min-w-0 flex-1 font-semibold truncate">
        {titles[page] || "Event Portal"}
      </h1>
      {editing && <button type="button" className="px-3 font-semibold" aria-label="Dismiss keyboard" onPointerDown={(event) => event.preventDefault()} onClick={() => document.activeElement?.blur()}>Done</button>}
      {isManagerView && (
        <span className="hidden sm:inline rounded bg-[rgb(var(--brand-yellow))] px-2 py-1 text-xs font-semibold text-black">
          Manager
        </span>
      )}
      {user && !editing && <>
        {assistant.visible && <button type="button" aria-label="Open assistant" title="Assistant" onClick={() => { if (allowNativeNavigation()) navigate('/assistant'); }}><Bot size={22} /></button>}
        <button type="button" aria-label="Send feedback" title="Feedback" onClick={() => window.dispatchEvent(new Event('sep-open-feedback'))}><MessageSquare size={21} /></button>
      </>}
      {user && <><DarkModeToggle /><NotificationCenter user={user} /></>}
    </header>
  );
}
